import { addMonths, format, isValid, parseISO } from 'date-fns'

/** Labra API keys that map to fields shown on the referral create form. */
export const REFERRAL_CREATE_FORM_LABRA_FIELD_KEYS = [
  'Name',
  'Labra__Company_Name__c',
  'Labra__Website__c',
  'Labra__Country__c',
  'Labra__State__c',
  'Labra__City__c',
  'Labra__Postal_Code__c',
  'Labra__Street_Address__c',
  'Labra__Customer_DUNS__c',
  'Labra__Industry__c',
  'Labra__Other_Industry__c',
  'Labra__Has_classified_NatSec_Information__c',
  'Labra__Customer_First_Name__c',
  'Labra__Customer_Last_Name__c',
  'Labra__Customer_Title__c',
  'Labra__Customer_Email__c',
  'Labra__Customer_Phone__c',
  'Labra__Expected_Monthly_AWS_Revenue__c',
  'Labra__Target_Close_Date__c',
  'Labra__Description__c',
  'Labra__Opportunity_Type__c',
  'Labra__Parent_Opportunity_ID__c',
  'Labra__Solution_offered__c',
  'Labra__Other_Solution_Offered__c',
  'Labra__Delivery_Model__c',
  'Labra__Use_Case__c',
  'Labra__AWS_Partner_Central_User_Email__c',
  'Labra__AWS_Partner_Central_User_Name__c',
  'Labra__Primary_Need_From_AWS_MultiSelect__c',
  'Labra__Sales_Activities__c',
  'Labra__Additional_Comments__c',
  'Labra__IsOppFromMarketingActivity__c',
  'Labra__MDF_Needed__c',
  'Labra__marketingActivityChannel__c',
  'Labra__marketingActivityUsecase__c',
  'Labra__awsSFCampaignName__c',
  'Labra__Campaign_Name__c',
  'Labra__AWS_products__c',
  'Labra__AWS_Account_Id__c',
  'Labra__Offer_ID__c',
  'Labra__Competitive_Tracking__c',
  'Labra__Primary_Sales_Rep_First_Name__c',
  'Labra__Primary_Sales_Rep_Last_Name__c',
  'Labra__Partner_Contact_Title__c',
  'Labra__Primary_Sales_Rep_Email__c',
  'Labra__Primary_Sales_Rep_Phone__c',
] as const

const CREATE_FORM_FIELD_KEY_SET = new Set<string>(
  REFERRAL_CREATE_FORM_LABRA_FIELD_KEYS
)

export const getReferralCloneDroppedFieldKeys = (
  labraReferral: Record<string, unknown>
): string[] =>
  Object.keys(labraReferral).filter(key => !CREATE_FORM_FIELD_KEY_SET.has(key))

export const bumpTargetCloseDateByOneMonth = (
  dateValue: unknown
): string | undefined => {
  if (dateValue == null || dateValue === '') {
    return undefined
  }

  const dateStr = String(dateValue).trim()
  const parsed = parseISO(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`)

  if (!isValid(parsed)) {
    return dateStr
  }

  return format(addMonths(parsed, 1), 'yyyy-MM-dd')
}

const stripTrailingCloneSuffix = (title: string): string =>
  title.replace(/\s*\(Clone\)\s*$/i, '').trim()

const extractParentReferralProjectTitle = (title: string): string => {
  let parentTitle = stripTrailingCloneSuffix(title.trim())
  parentTitle = parentTitle.replace(/^Clone\s*\(/i, '').replace(/\)\s*$/i, '')
  parentTitle = parentTitle.replace(/^Clone\s+/i, '')
  return parentTitle.trim() || 'Referral'
}

export const formatClonedReferralProjectTitle = (sourceTitle: string): string => {
  const formatted = `Clone ${extractParentReferralProjectTitle(sourceTitle)}`
  const trimmed = sourceTitle.trim()
  return trimmed === formatted ? trimmed : formatted
}

export const prepareLabraReferralObjectForClone = (
  labraReferral: Record<string, unknown>,
  sourceTitle?: string
): Record<string, unknown> => {
  const cloned: Record<string, unknown> = {}

  for (const field of REFERRAL_CREATE_FORM_LABRA_FIELD_KEYS) {
    if (field === 'Name') {
      continue
    }
    if (field in labraReferral) {
      cloned[field] = labraReferral[field]
    }
  }

  const baseName =
    String(labraReferral.Name ?? sourceTitle ?? 'Referral').trim() || 'Referral'
  cloned.Name = formatClonedReferralProjectTitle(baseName)

  if (cloned.Labra__Target_Close_Date__c != null) {
    cloned.Labra__Target_Close_Date__c = bumpTargetCloseDateByOneMonth(
      cloned.Labra__Target_Close_Date__c
    )
  }

  return cloned
}

export const getHubspotCloneSourceReferralId = (
  search: string
): string | undefined => {
  const value = new URLSearchParams(search)
    .get('clone_source_referral_id')
    ?.trim()
  return value || undefined
}

export const isHubspotReferralCloneCreateFlow = (search: string): boolean =>
  new URLSearchParams(search).get('from') === 'clone' &&
  Boolean(getHubspotCloneSourceReferralId(search))

export const getHubspotCloneCloudAccountId = (
  search: string
): string | undefined => {
  const value = new URLSearchParams(search)
    .get('clone_cloud_account_id')
    ?.trim()
  return value || undefined
}

export const getHubspotCloneCloudType = (search: string): string => {
  const value = new URLSearchParams(search)
    .get('clone_cloud_type')
    ?.trim()
    .toLowerCase()
  return value || 'aws'
}
